import { useMemo, useRef, useState } from "react";

import { Comment } from "@/api";
import { cn } from "@/utils";

import { usePutComment } from "../hooks/usePutComment.tsx";
import { CommentInput } from "./CommentInput.tsx";
import { SingleComment } from "./SingleComment.tsx";
import { useCommentsContext } from "../providers/context/useCommentsContext.tsx";

interface CommentWithResponsesProps {
  comment: Comment;
  topLevel?: boolean;
  lastReply?: boolean;
}

/* Recursive until there are no more Responses */
export const CommentWithResponses = ({
  comment,
  topLevel,
  lastReply,
}: CommentWithResponsesProps) => {
  const {
    setPendingComment,
    defaultPendingComment,
    userThumbnail,
    dataSource,
    itemId,
    itemType,
    sourceId,
  } = useCommentsContext();

  const [replying, setReplying] = useState(false);
  const [showReplies, setShowReplies] = useState(false);
  const putComment = usePutComment();
  const sortedResponses = useMemo(() => {
    return comment.Responses.sort((a, b) =>
      a.CommentDate.localeCompare(b.CommentDate),
    );
  }, [comment.Responses]);

  const isExpanded = replying || showReplies;

  const inputRef = useRef<HTMLInputElement>(null);

  return (
    <div className="flex flex-col">
      <SingleComment
        comment={comment}
        setReplying={setReplying}
        setShowReplies={setShowReplies}
        topLevel={topLevel}
        lastReply={lastReply}
        inputRef={inputRef}
      />
      {(replying || comment.Responses?.length > 0) && (
        <div className={cn("flex flex-col", isExpanded && "mt-10")}>
          {showReplies && (
            <div className="flex flex-col">
              {sortedResponses?.map((r, idx) => (
                <CommentWithResponses
                  key={r.SourceCommentId}
                  comment={r}
                  lastReply={idx === sortedResponses.length - 1 && !replying}
                />
              ))}
            </div>
          )}
          {replying && (
            <CommentInput
              inputRef={inputRef}
              putComment={(c) => {
                setPendingComment({
                  ...defaultPendingComment,
                  Comment: c,
                  CommentDate: new Date().toISOString(),
                  ParentCommentId: comment.SourceCommentId,
                });

                putComment.mutate(
                  {
                    comment: c,
                    dataSource,
                    itemId,
                    itemType,
                    sourceId,
                    parentCommentId: comment.SourceCommentId,
                    thumbnailSource: userThumbnail,
                  },
                  {
                    onSuccess: () => setPendingComment(null),
                    onError: () => setPendingComment(null),
                  },
                );
              }}
              onCancel={() => setReplying(false)}
            />
          )}
        </div>
      )}
    </div>
  );
};
